import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import axios from "axios";
import { toast } from "sonner";
import { REGION_STYLE } from "@/lib/report";
import { Card } from "@/components/ui/card";
import { Select, SelectTrigger, SelectContent, SelectItem } from "@/components/ui/select";
import { thumbUrl } from "@/components/DynamicForm";
import { Plus, X, Loader2, CheckCircle2, XCircle, Clock, Pencil, Trash2, RotateCcw } from "lucide-react";
import { useVisibilityPolling } from "@/hooks/useVisibilityPolling";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NONE = "__none__";

const MARKS = [
  { val: "√", label: "Yes", Icon: CheckCircle2, color: "#1F3FD1", slug: "tick" },
  { val: "✗", label: "No", Icon: XCircle, color: "#C00000", slug: "cross" },
  { val: "Pending", label: "Pending", Icon: Clock, color: "#E67E00", slug: "pending" },
];
const MARK_BY_VAL = Object.fromEntries(MARKS.map((m) => [m.val, m]));

// Icon mapping for special evaluation option values
const EVALUATION_ICON_MAP = {
  "No Participation": { Icon: XCircle, color: "#C00000" },
};

function MarkCell({ report, outlet, code, colKey, value, hold, onSaved, onLocal }) {
  const change = async (v) => {
    const next = v === NONE ? "" : v;
    onLocal(next);
    hold(true);
    try {
      await axios.post(`${API}/reports/${report.id}/grid-cell`, { outlet, field: colKey, value: next });
    } catch { toast.error("Failed to save"); }
    finally { hold(false); onSaved(); }
  };
  const cur = MARK_BY_VAL[value];
  return (
    <Select value={value || NONE} onValueChange={change}>
      <SelectTrigger data-testid={`cell-${code}-${colKey}`}
        className="h-8 w-[70px] mx-auto bg-white/80 border-slate-200 px-2 [&>span]:flex [&>span]:justify-center [&>span]:w-full">
        {cur ? <cur.Icon className="w-[18px] h-[18px]" style={{ color: cur.color }} strokeWidth={2.4} />
             : <span className="text-slate-300 text-sm">—</span>}
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE} data-testid={`cell-${code}-${colKey}-none`}>
          <span className="text-slate-400">— None</span>
        </SelectItem>
        {MARKS.map((m) => (
          <SelectItem key={m.val} value={m.val} data-testid={`cell-${code}-${colKey}-${m.slug}`}>
            <span className="flex items-center gap-2 font-semibold" style={{ color: m.color }}>
              <m.Icon className="w-4 h-4" strokeWidth={2.4} />{m.label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function OptionCell({ report, outlet, code, colKey, value, options, colored, iconMap, hold, onSaved, onLocal }) {
  const statusColors = (report && report.status_colors) || {};
  const icons = iconMap || {};
  const iconDef = icons[value];
  const valueColor = iconDef ? iconDef.color : (colored && value ? statusColors[value] : null);

  const change = async (selected) => {
    const next = selected === NONE ? "" : selected;
    onLocal(next);
    hold(true);
    try {
      await axios.post(`${API}/reports/${report.id}/grid-cell`, { outlet, field: colKey, value: next });
    } catch { toast.error("Failed to save"); }
    finally { hold(false); onSaved(); }
  };
  return (
    <Select value={value || NONE} onValueChange={change}>
      <SelectTrigger data-testid={`cell-${code}-${colKey}`}
        className="h-8 min-w-[150px] bg-white/80 border-slate-200 px-2 text-xs font-semibold">
        {iconDef
          ? <span className="flex items-center gap-1.5 font-semibold" style={{ color: iconDef.color }}>
              <iconDef.Icon className="w-[15px] h-[15px] shrink-0" strokeWidth={2.4} />
              <span>{value}</span>
            </span>
          : <span style={valueColor ? { color: valueColor, fontWeight: 700 } : undefined}
              className={value && !valueColor ? "text-slate-700" : value ? "" : "text-slate-300"}>
              {value || "—"}
            </span>}
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE} data-testid={`cell-${code}-${colKey}-none`}>
          <span className="text-slate-400">— None</span>
        </SelectItem>
        {(options || []).map((option) => {
          const slug = option.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
          const optIcon = icons[option];
          const optColor = optIcon ? optIcon.color : (colored ? statusColors[option] : null);
          return (
            <SelectItem key={option} value={option} data-testid={`cell-${code}-${colKey}-${slug}`}>
              {optIcon
                ? <span className="flex items-center gap-2 font-semibold" style={{ color: optIcon.color }}>
                    <optIcon.Icon className="w-4 h-4" strokeWidth={2.4} />{option}
                  </span>
                : <span style={optColor ? { color: optColor, fontWeight: 700 } : undefined}>{option}</span>}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

function ImageCell({ report, outlet, code, colKey, value, hold, onSaved, landscape = false, withCaption = true }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const arr = Array.isArray(value) ? value : (value ? [value] : []);
  const item = arr[0] && typeof arr[0] === "object" ? arr[0] : (arr[0] ? { path: arr[0] } : {});
  const hasImg = !!item.path;
  const testPrefix = landscape ? "sheet-photo" : "bulb";
  const [caption, setCaption] = useState(item.caption || "");
  const capDirty = useRef(false);
  useEffect(() => { if (!capDirty.current) setCaption(item.caption || ""); }, [item.caption]);

  const persist = async (next) => {
    await axios.post(`${API}/reports/${report.id}/grid-cell`, { outlet, field: colKey, value: next });
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload`, fd);
      await persist([{ path: res.data.path, scale: 1, caption }]);
      onSaved();
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };
  const remove = async (ev) => {
    ev.stopPropagation();
    setBusy(true);
    try { await persist(caption ? [{ path: "", scale: 1, caption }] : []); onSaved(); }
    catch { toast.error("Failed to remove"); }
    finally { setBusy(false); }
  };
  const saveCaption = async () => {
    hold(false);
    if (!capDirty.current) return;
    capDirty.current = false;
    try {
      const next = (hasImg || caption) ? [{ path: item.path || "", scale: item.scale || 1, caption }] : [];
      await persist(next); onSaved();
    } catch { toast.error("Failed to save"); }
  };

  return (
    <div className="flex flex-col items-center gap-1">
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={upload} data-testid={`${testPrefix}-input-${code}-${colKey}`} />
      <button type="button" onClick={() => inputRef.current?.click()} data-testid={`${testPrefix}-box-${code}-${colKey}`}
        title={hasImg ? "Click to replace photo" : "Click to upload photo"}
        className={`relative ${landscape ? "w-28 aspect-video" : "w-16 h-16"} rounded-lg border-2 border-dashed border-slate-300 bg-white hover:border-amber-400 hover:bg-amber-50/40 transition flex items-center justify-center overflow-hidden group`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
          : hasImg ? (
            <>
              <img src={thumbUrl(item.path)} alt="" className="w-full h-full object-cover" />
              <span onClick={remove} data-testid={`${testPrefix}-remove-${code}-${colKey}`}
                className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                <X className="w-2.5 h-2.5" />
              </span>
            </>
          ) : <Plus className="w-5 h-5 text-slate-300 group-hover:text-amber-500 transition" />}
      </button>
      {withCaption && <input type="text" value={caption} placeholder="note…" data-testid={`${testPrefix}-note-${code}-${colKey}`}
        onFocus={() => hold(true)}
        onChange={(e) => { capDirty.current = true; setCaption(e.target.value); }}
        onBlur={saveCaption}
        className="w-16 text-center text-[11px] py-0.5 rounded border border-transparent bg-white/70 hover:border-slate-300 focus:border-amber-500 focus:bg-white outline-none" />}
    </div>
  );
}

function TextCell({ report, outlet, code, colKey, value, multiline, hold, onSaved }) {
  const [local, setLocal] = useState(value ?? "");
  const dirtyRef = useRef(false);
  useEffect(() => { if (!dirtyRef.current) setLocal(value ?? ""); }, [value]);
  const commit = async () => {
    hold(false);
    if (!dirtyRef.current) return;
    dirtyRef.current = false;
    try { await axios.post(`${API}/reports/${report.id}/grid-cell`, { outlet, field: colKey, value: local }); }
    catch { toast.error("Failed to save"); }
    finally { onSaved(); }
  };
  const common = {
    value: local,
    "data-testid": `cell-${code}-${colKey}`,
    onFocus: () => hold(true),
    onChange: (e) => { dirtyRef.current = true; setLocal(e.target.value); },
    onBlur: commit,
    className: "w-full text-sm py-1.5 px-2 rounded-md border border-transparent bg-white/70 hover:border-slate-300 focus:border-amber-500 focus:bg-white outline-none",
  };
  return multiline
    ? <textarea rows={2} {...common} className={common.className + " resize-y min-h-[38px]"} />
    : <input type="text" {...common} />;
}

function EditableHeader({ report, colKey, label, onSaved, onDelete, locked = false }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(label);
  useEffect(() => { setVal(label); }, [label]);
  const isCustom = colKey.startsWith("extra_");
  if (!report.columns_editable || locked) return <span data-testid={`colhead-${colKey}`}>{label}</span>;

  const save = async () => {
    setEditing(false);
    const t = val.trim();
    if (!t || t === label) { setVal(label); return; }
    try {
      await axios.put(`${API}/reports/${report.id}/column-label`, { field: colKey, label: t });
      onSaved && onSaved();
    } catch { toast.error("Failed to rename"); setVal(label); }
  };

  if (editing) {
    return (
      <input autoFocus value={val} onChange={(e) => setVal(e.target.value)} onBlur={save}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setVal(label); setEditing(false); } }}
        data-testid={`colhead-input-${colKey}`}
        className="w-28 text-center text-xs px-1 py-0.5 rounded border border-amber-400 outline-none normal-case" />
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <button type="button" onClick={() => setEditing(true)} title="Click to rename column" data-testid={`colhead-${colKey}`}
        className="inline-flex items-center gap-1 hover:text-amber-600 transition group">
        <span>{label}</span>
        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-60 transition" />
      </button>
      {isCustom && (
        <button type="button" onClick={(event) => { event.stopPropagation(); onDelete(colKey, label); }}
          title={`Remove ${label} column`} aria-label={`Remove ${label} column`}
          data-testid={`colhead-delete-${colKey}`}
          className="relative z-10 inline-flex h-7 w-7 shrink-0 touch-manipulation items-center justify-center rounded-md border border-transparent text-slate-400 transition-[color,background-color,border-color] hover:border-red-200 hover:bg-red-50 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400">
          <Trash2 className="w-3.5 h-3.5 pointer-events-none" />
        </button>
      )}
    </span>
  );
}

function AddColumnHead({ report, onAdded }) {
  const [adding, setAdding] = useState(false);
  const [val, setVal] = useState("");
  const save = async () => {
    const t = val.trim();
    setAdding(false); setVal("");
    if (!t) return;
    try { await axios.post(`${API}/reports/${report.id}/column`, { label: t }); onAdded && onAdded(); }
    catch { toast.error("Failed to add column"); }
  };
  return adding ? (
    <input autoFocus value={val} placeholder="New column…" onChange={(e) => setVal(e.target.value)} onBlur={save}
      onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setVal(""); setAdding(false); } }}
      data-testid="add-column-input"
      className="w-28 text-center text-xs px-1 py-0.5 rounded border border-amber-400 outline-none normal-case" />
  ) : (
    <button type="button" onClick={() => setAdding(true)} title="Add a column" data-testid="add-column-btn"
      className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-dashed border-slate-300 text-slate-400 hover:border-amber-400 hover:text-amber-500 transition">
      <Plus className="w-4 h-4" />
    </button>
  );
}

export default function OutletSheetGrid({ report, onConfigChanged, highlightedId }) {
  const [grid, setGrid] = useState(null);
  const signatureRef = useRef("");
  const holdRef = useRef(false);

  const photoCol = report.photo_left_of_code ? (report.columns || []).find((c) => c.key === "photo" && c.type === "image") : null;
  const dataCols = (report.columns || []).filter((c) => c.key !== "code" && c.key !== "outlet" && c.key !== photoCol?.key);
  const trailingKeys = new Set(report.sheet_trailing_columns || []);
  const cols = dataCols.filter((c) => !trailingKeys.has(c.key));
  const trailingCols = dataCols.filter((c) => trailingKeys.has(c.key));
  const hold = (b) => { holdRef.current = b; };
  const flexKey = [...cols].reverse().find((c) => c.type === "textarea")?.key || null;
  const showPlus = !!report.add_columns;
  const showSpacer = !showPlus && !flexKey && trailingCols.length === 0;
  const span = 2 + (photoCol ? 1 : 0) + cols.length + trailingCols.length + (showPlus ? 1 : 0) + (showSpacer ? 1 : 0);
  const colsKey = dataCols.map((c) => c.key + ":" + c.label).join("|");

  const load = useCallback(async (notify = false) => {
    const res = await axios.get(`${API}/reports/${report.id}/grid-sheet`);
    const sig = JSON.stringify(res.data);
    const changed = Boolean(signatureRef.current) && sig !== signatureRef.current;
    if (holdRef.current) return;
    signatureRef.current = sig;
    setGrid(res.data);
    if (notify && changed) toast.success(<span data-testid="sheet-sync-toast">New data synced</span>, { id: "sheet-auto-sync" });
  }, [report.id]);

  useEffect(() => { setGrid(null); signatureRef.current = ""; load(); }, [load, colsKey]);

  const syncGrid = useCallback(async () => {
    try { await load(true); }
    catch (e) { if (e?.response?.status !== 401) console.error("Sheet auto sync failed", e); }
  }, [load]);
  useVisibilityPolling(syncGrid, true, 5000);

  const setLocalCell = (region, outlet, field, v) => {
    setGrid((g) => ({
      ...g,
      regions: g.regions.map((rg) => rg.region !== region ? rg : {
        ...rg, outlets: rg.outlets.map((o) => o.outlet !== outlet ? o : { ...o, [field]: v }),
      }),
    }));
  };

  const deleteColumn = async (key, label) => {
    if (!window.confirm(`Remove column "${label}"? Its data will be cleared.`)) return;
    try { await axios.delete(`${API}/reports/${report.id}/column/${key}`); onConfigChanged && onConfigChanged(); }
    catch { toast.error("Failed to remove column"); }
  };

  const deleteRow = async (outlet) => {
    if (!window.confirm(`Remove the "${outlet}" row from this report? Its data will be cleared.`)) return;
    try {
      await axios.post(`${API}/reports/${report.id}/hide-outlet`, { outlet });
      toast.success(`${outlet} row removed`);
      onConfigChanged && onConfigChanged();
      load();
    } catch { toast.error("Failed to remove row"); }
  };

  const restoreRow = async (outlet) => {
    try {
      await axios.post(`${API}/reports/${report.id}/unhide-outlet`, { outlet });
      toast.success(`${outlet} row restored`);
      onConfigChanged && onConfigChanged();
      load();
    } catch { toast.error("Failed to restore row"); }
  };

  if (!grid) return <div className="py-24 text-center text-slate-400" data-testid="sheet-loading">Loading…</div>;

  return (
    <Card className="p-5 shadow-sm border-slate-200">
      <p className="text-xs text-slate-400 mb-3">
        Edit any cell directly — changes save automatically. Click a column header to rename it{showPlus ? ", or use + on the right to add one." : "."}
        {" "}Hover a row and use the bin icon to remove that outlet from this report.
      </p>
      {(report.hidden_outlets || []).length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-slate-500" data-testid="hidden-rows-bar">
          <span className="font-semibold uppercase tracking-wide text-[10px] text-slate-400">Removed rows</span>
          {report.hidden_outlets.map((name) => (
            <button key={name} type="button" onClick={() => restoreRow(name)} data-testid={`restore-row-${name}`}
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 font-medium hover:border-amber-300 hover:text-amber-600 transition-colors">
              <RotateCcw className="w-3 h-3" />{name}
            </button>
          ))}
        </div>
      )}
      <div className="overflow-auto max-h-[calc(100vh-220px)]">
        <table className="w-full text-sm border-collapse" data-testid="sheet-grid">
          <thead className="sticky top-0 z-20 bg-white">
            <tr className="border-b-2 border-slate-200">
              {photoCol && <th className="py-2 px-2 text-center text-[11px] font-bold uppercase text-slate-500 whitespace-nowrap bg-white" data-testid="sheet-photo-header">{photoCol.label}</th>}
              <th className="py-2 px-2 text-left text-[11px] font-bold uppercase text-slate-500 bg-white" data-testid="sheet-code-header">Code</th>
              <th className="py-2 px-2 text-left text-[11px] font-bold uppercase text-slate-500 whitespace-nowrap bg-white" data-testid="sheet-outlet-header">Outlet</th>
              {cols.map((c) => (
                <th key={c.key}
                  style={c.key === flexKey ? { width: "100%", minWidth: 240 } : undefined}
                  className={`py-2 px-2 text-[11px] font-bold uppercase text-slate-500 whitespace-nowrap bg-white ${c.key === flexKey ? "text-left" : "text-center"}`}>
                  <EditableHeader report={report} colKey={c.key} label={c.label} locked={c.fixed_label}
                    onSaved={onConfigChanged} onDelete={deleteColumn} />
                </th>
              ))}
              {showPlus && <th className="py-2 px-2 text-center bg-white" data-testid="sheet-add-column-slot">
                <AddColumnHead report={report} onAdded={onConfigChanged} />
              </th>}
              {trailingCols.map((c) => (
                <th key={c.key} className="py-2 px-2 text-center text-[11px] font-bold uppercase text-slate-500 whitespace-nowrap bg-white">
                  <EditableHeader report={report} colKey={c.key} label={c.label} locked={c.fixed_label}
                    onSaved={onConfigChanged} onDelete={deleteColumn} />
                </th>
              ))}
              {showSpacer && <th className="py-2 px-2 text-center bg-white" style={{ width: "100%" }} />}
            </tr>
          </thead>
          <tbody>
            {grid.regions.map((rg) => (
              <Fragment key={rg.region}>
                <tr>
                  <td colSpan={span} className="py-1.5 px-3 font-bold text-white text-center"
                    style={{ backgroundColor: REGION_STYLE[rg.region].banner }} data-testid={`sheet-region-${rg.region}`}>{rg.region}</td>
                </tr>
                {rg.outlets.map((o) => (
                  <tr key={o.outlet} data-search-record-id={o.id || undefined}
                    data-testid={o.id ? `sheet-record-row-${o.id}` : undefined}
                    className={`group border-b border-slate-100 transition-[box-shadow] duration-300 ${o.id && o.id === highlightedId ? "relative z-10 ring-4 ring-inset ring-amber-400 shadow-[0_0_0_3px_rgba(251,191,36,0.22)]" : ""}`}
                    style={{ backgroundColor: REGION_STYLE[rg.region].tint }}>
                    {photoCol && <td className="py-1 px-1.5 align-middle">
                      <ImageCell report={report} outlet={o.outlet} code={o.code} colKey={photoCol.key} value={o[photoCol.key]}
                        hold={hold} onSaved={load} landscape withCaption={false} />
                    </td>}
                    <td className="py-1.5 px-2 font-semibold text-slate-600 text-xs whitespace-nowrap" data-testid={`sheet-code-${o.code}`}>
                      <span className="inline-flex items-center gap-1">
                        <button type="button" onClick={(event) => { event.stopPropagation(); deleteRow(o.outlet); }}
                          data-testid={`delete-row-${o.code}`} aria-label={`Remove the ${o.outlet} row from this report`}
                          title={`Remove the ${o.outlet} row from this report`}
                          className="relative z-10 inline-flex h-7 w-7 shrink-0 touch-manipulation items-center justify-center rounded-md text-slate-400 opacity-60 transition-[opacity,color,background-color] hover:bg-red-50 hover:text-red-600 hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 sm:opacity-20 sm:group-hover:opacity-100">
                          <Trash2 className="w-3.5 h-3.5 pointer-events-none" />
                        </button>
                        {o.code}
                      </span>
                    </td>
                    <td className="py-1.5 px-2 font-medium text-slate-800 whitespace-nowrap" data-testid={`sheet-outlet-${o.code}`}>{o.outlet}</td>
                    {cols.map((c) => (
                      <td key={c.key} className="py-1 px-1.5 align-middle">
                        {c.type === "image" ? (
                          <ImageCell report={report} outlet={o.outlet} code={o.code} colKey={c.key} value={o[c.key]} hold={hold} onSaved={load} />
                        ) : c.type === "select" ? (
                          (c.options || []).every((option) => MARK_BY_VAL[option])
                            ? <MarkCell report={report} outlet={o.outlet} code={o.code} colKey={c.key}
                                value={o[c.key]} hold={hold} onSaved={load} onLocal={(v) => setLocalCell(rg.region, o.outlet, c.key, v)} />
                            : <OptionCell report={report} outlet={o.outlet} code={o.code} colKey={c.key}
                                value={o[c.key]} options={c.options} colored={c.colored} hold={hold} onSaved={load}
                                onLocal={(v) => setLocalCell(rg.region, o.outlet, c.key, v)} />
                        ) : (
                          <TextCell report={report} outlet={o.outlet} code={o.code} colKey={c.key} value={o[c.key]}
                            multiline={c.type === "textarea"} hold={hold} onSaved={load} />
                        )}
                      </td>
                    ))}
                    {showPlus && <td data-testid={`sheet-add-column-cell-${o.code}`} />}
                    {trailingCols.map((c) => (
                      <td key={c.key} className="py-1 px-1.5 align-middle">
                        {c.type === "select" ? (
                          <OptionCell report={report} outlet={o.outlet} code={o.code} colKey={c.key}
                            value={o[c.key]} options={c.options} colored={c.colored}
                            iconMap={c.key === "evaluation" ? EVALUATION_ICON_MAP : undefined}
                            hold={hold} onSaved={load}
                            onLocal={(v) => setLocalCell(rg.region, o.outlet, c.key, v)} />
                        ) : (
                          <TextCell report={report} outlet={o.outlet} code={o.code} colKey={c.key} value={o[c.key]}
                            multiline={c.type === "textarea"} hold={hold} onSaved={load} />
                        )}
                      </td>
                    ))}
                    {showSpacer && <td />}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
